import { APP_CONFIG } from '../config/theme';
import { FirebaseService } from './firebaseService';
import ProcessConversionService from './processConversionService';
import backgroundSyncService from './backgroundSyncService';
import SyncHistoryService from './syncHistoryService';

/**
 * Serviço para gerenciar sincronização automática com PJe
 */
export class SyncService {
  
  /**
   * Executar sincronização inicial para um usuário
   */
  static async executeInitialSync(userId, oabs, startDate, endDate) {
    console.log(`🔄 Iniciando sincronização inicial para usuário ${userId}`);
    
    const results = {
      totalComunicacoes: 0,
      sucessos: 0,
      erros: 0,
      detalhes: []
    };

    for (const oab of oabs) {
      try {
        console.log(`📡 Sincronizando OAB ${oab.numero}/${oab.seccional}...`);
        
        const comunicacoes = await this.fetchPjeComunicacoes({
          numeroOab: oab.numero,
          ufOab: oab.seccional,
          dataInicio: startDate,
          dataFim: endDate
        });

        if (comunicacoes.length > 0) {
          // Salvar comunicações
          await FirebaseService.savePjeComunicacoes(userId, oab, comunicacoes);

          // Processamento automático com IA (se habilitado)
          const processedCount = await this.processWithAIIfEnabled(userId, comunicacoes);

          results.totalComunicacoes += comunicacoes.length;
          results.processedWithAI = (results.processedWithAI || 0) + processedCount;
          results.sucessos++;

          results.detalhes.push({
            oab: `${oab.numero}/${oab.seccional}`,
            status: 'sucesso',
            quantidade: comunicacoes.length,
            processedWithAI: processedCount
          });
        } else {
          results.detalhes.push({
            oab: `${oab.numero}/${oab.seccional}`,
            status: 'sem_dados',
            quantidade: 0
          });
        }

        // Delay entre requisições para respeitar rate limit
        await this.delay(2000);
        
      } catch (error) {
        console.error(`❌ Erro ao sincronizar OAB ${oab.numero}/${oab.seccional}:`, error);
        results.erros++;
        
        results.detalhes.push({
          oab: `${oab.numero}/${oab.seccional}`,
          status: 'erro',
          erro: error.message
        });
      }
    }

    console.log(`✅ Sincronização inicial concluída:`, results);
    return results;
  }

  /**
   * Executar sincronização diária (últimas 24 horas ou período configurado)
   */
  static async executeDailySync(userId, oabs) {
    try {
      // Verificar se há configurações de período inicial
      const monitoringSettings = await FirebaseService.getMonitoringSettings(userId);

      let startDate, endDate;

      if (monitoringSettings && monitoringSettings.startDate && monitoringSettings.endDate) {
        // Usar período configurado pelo usuário
        startDate = monitoringSettings.startDate;
        endDate = monitoringSettings.endDate;
        console.log(`🔄 Executando sincronização com período configurado para usuário ${userId} (${startDate} a ${endDate})`);
      } else {
        // Usar período padrão (últimos 3 dias para garantir que pega tudo)
        const hoje = new Date();
        const tresDiasAtras = new Date(hoje.getTime() - 3 * 24 * 60 * 60 * 1000);

        startDate = tresDiasAtras.toISOString().split('T')[0];
        endDate = hoje.toISOString().split('T')[0];

        console.log(`🔄 Executando sincronização diária para usuário ${userId}`);
        console.log(`📅 Período: ${startDate} (${tresDiasAtras.toLocaleDateString('pt-BR')}) até ${endDate} (${hoje.toLocaleDateString('pt-BR')})`);
        console.log(`🕐 Horário atual: ${hoje.toLocaleString('pt-BR')}`);
      }

      return await this.executeInitialSync(userId, oabs, startDate, endDate);
    } catch (error) {
      console.error('❌ Erro ao executar sincronização diária:', error);
      throw error;
    }
  }

  /**
   * Executar sincronização inteligente (com controle de duplicatas)
   */
  static async executeSmartSync(userId, oabs, tipoSync = 'manual', customPeriod = null) {
    console.log(`🔄 Iniciando sincronização inteligente para usuário ${userId} (${tipoSync})`);
    
    const results = {
      totalComunicacoes: 0,
      novasComunicacoes: 0,
      comunicacoesDuplicadas: 0,
      sucessos: 0,
      erros: 0,
      detalhes: [],
      periodos: []
    };

    for (const oab of oabs) {
      const inicioOab = Date.now();
      
      try {
        console.log(`📡 Sincronizando OAB ${oab.numero}/${oab.seccional}...`);
        
        // Calcular período (customizado ou inteligente baseado na última sincronização)
        let periodoSync;
        if (customPeriod) {
          periodoSync = {
            dataInicio: customPeriod.startDate,
            dataFim: customPeriod.endDate,
            isFirstSync: false,
            isRecovery: false,
            isCustom: true
          };
        } else {
          periodoSync = await SyncHistoryService.calculateNextSyncPeriod(userId, oab, tipoSync);
        }
        
        console.log(`📅 Período calculado para OAB ${oab.numero}/${oab.seccional}:`, periodoSync);
        console.log(`🌐 Fazendo busca na API PJe com parâmetros:`, {
          numeroOab: oab.numero,
          ufOab: oab.seccional,
          dataInicio: periodoSync.dataInicio,
          dataFim: periodoSync.dataFim
        });
        
        const comunicacoes = await this.fetchPjeComunicacoes({
          numeroOab: oab.numero,
          ufOab: oab.seccional,
          dataInicio: periodoSync.dataInicio,
          dataFim: periodoSync.dataFim
        });
        
        console.log(`📊 API PJe retornou ${comunicacoes.length} comunicações para OAB ${oab.numero}/${oab.seccional}`);

        // Verificar comunicações duplicadas antes de salvar
        const comunicacoesExistentes = await FirebaseService.getUserPjeComunicacoes(userId, {
          oabNumero: oab.numero,
          oabSeccional: oab.seccional
        });
        
        const idsExistentes = new Set(comunicacoesExistentes.map(c => c.id));
        const comunicacoesNovas = comunicacoes.filter(c => !idsExistentes.has(c.id));
        const comunicacoesDuplicadas = comunicacoes.length - comunicacoesNovas.length;

        console.log(`📊 OAB ${oab.numero}/${oab.seccional}: ${comunicacoes.length} encontradas, ${comunicacoesNovas.length} novas, ${comunicacoesDuplicadas} duplicadas`);

        if (comunicacoesNovas.length > 0) {
          // Salvar apenas comunicações novas
          await FirebaseService.savePjeComunicacoes(userId, oab, comunicacoesNovas);

          // Processamento automático com IA (se habilitado)
          const processedCount = await this.processWithAIIfEnabled(userId, comunicacoesNovas);
          results.processedWithAI = (results.processedWithAI || 0) + processedCount;
        }

        // Registrar resultado da sincronização
        const duracaoMs = Date.now() - inicioOab;
        const syncRecord = await SyncHistoryService.saveSyncRecord(userId, oab, {
          dataInicio: periodoSync.dataInicio,
          dataFim: periodoSync.dataFim,
          totalComunicacoes: comunicacoes.length,
          novasComunicacoes: comunicacoesNovas.length,
          comunicacoesDuplicadas,
          comunicacoesProcessadas: results.processedWithAI || 0,
          status: 'success',
          tipoSync,
          duracaoMs,
          iniciadoEm: new Date(inicioOab).toISOString()
        });

        results.totalComunicacoes += comunicacoes.length;
        results.novasComunicacoes += comunicacoesNovas.length;
        results.comunicacoesDuplicadas += comunicacoesDuplicadas;
        results.sucessos++;

        results.detalhes.push({
          oab: `${oab.numero}/${oab.seccional}`,
          status: 'sucesso',
          periodo: `${periodoSync.dataInicio} até ${periodoSync.dataFim}`,
          totalEncontradas: comunicacoes.length,
          novas: comunicacoesNovas.length,
          duplicadas: comunicacoesDuplicadas,
          isFirstSync: periodoSync.isFirstSync,
          isRecovery: periodoSync.isRecovery,
          duracaoMs
        });

        results.periodos.push({
          oab: `${oab.numero}/${oab.seccional}`,
          ...periodoSync,
          syncId: syncRecord.id
        });

        // Delay entre requisições para respeitar rate limit
        await this.delay(2000);
        
      } catch (error) {
        console.error(`❌ Erro ao sincronizar OAB ${oab.numero}/${oab.seccional}:`, error);
        
        // Registrar erro na história
        const duracaoMs = Date.now() - inicioOab;
        try {
          await SyncHistoryService.saveSyncRecord(userId, oab, {
            dataInicio: new Date().toISOString().split('T')[0],
            dataFim: new Date().toISOString().split('T')[0],
            totalComunicacoes: 0,
            novasComunicacoes: 0,
            comunicacoesDuplicadas: 0,
            status: 'failed',
            erro: error.message,
            tipoSync,
            duracaoMs,
            iniciadoEm: new Date(inicioOab).toISOString()
          });
        } catch (historyError) {
          console.error('❌ Erro ao salvar histórico de erro:', historyError);
        }
        
        results.erros++;
        results.detalhes.push({
          oab: `${oab.numero}/${oab.seccional}`,
          status: 'erro',
          erro: error.message,
          duracaoMs
        });
      }
    }

    console.log(`✅ Sincronização inteligente concluída:`, results);
    return results;
  }

  /**
   * Buscar comunicações na API PJe
   */
  static async fetchPjeComunicacoes(params) {
    const queryParams = new URLSearchParams();

    if (params.numeroOab) queryParams.append('numeroOab', params.numeroOab);
    if (params.ufOab) queryParams.append('ufOab', params.ufOab.toString().toUpperCase());
    if (params.dataInicio) queryParams.append('dataDisponibilizacaoInicio', params.dataInicio);
    if (params.dataFim) queryParams.append('dataDisponibilizacaoFim', params.dataFim);
    queryParams.append('itensPorPagina', '100');

    const apiUrl = `${APP_CONFIG.pjeApi.baseUrl}${APP_CONFIG.pjeApi.comunicacao}?${queryParams.toString()}`;

    // Debug da requisição
    console.log('🌐 Fazendo requisição para API PJe:');
    console.log('📋 Parâmetros:', {
      numeroOab: params.numeroOab,
      ufOab: params.ufOab,
      dataInicio: params.dataInicio,
      dataFim: params.dataFim,
      dataInicioFormatada: new Date(params.dataInicio).toLocaleDateString('pt-BR'),
      dataFimFormatada: new Date(params.dataFim).toLocaleDateString('pt-BR')
    });
    console.log('🔗 URL:', apiUrl);

    try {
      console.log(`🚀 Fazendo requisição HTTP para: ${apiUrl}`);
      
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      });
      
      console.log(`📡 Resposta HTTP recebida - Status: ${response.status} ${response.statusText}`);

      if (response.status === 429) {
        throw new Error("Rate limit atingido. Aguardando...");
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ 
          message: `Erro HTTP ${response.status}: ${response.statusText}` 
        }));
        throw new Error(`Falha na API PJe: ${response.status} - ${errorData?.message || 'Erro desconhecido'}`);
      }

      const data = await response.json();
      console.log('📊 Dados recebidos da API PJe:', data);
      console.log('🔍 Estrutura da resposta:', {
        hasItems: !!data.items,
        itemsType: Array.isArray(data.items) ? 'array' : typeof data.items,
        itemsLength: data.items?.length || 0,
        totalElements: data.totalElements,
        totalPages: data.totalPages,
        keys: Object.keys(data || {})
      });

      if (data && Array.isArray(data.items)) {
        const comunicacoes = data.items.map(item => ({
          ...item,
          id: item.id || item.hash || `${item.numero_processo}_${item.data_disponibilizacao}`,
          fetchedAt: new Date().toISOString()
        }));

        // Debug das comunicações retornadas
        console.log(`📄 API retornou ${comunicacoes.length} comunicações`);

        if (comunicacoes.length > 0) {
          // Agrupar por data para debug
          const porData = {};
          comunicacoes.forEach(com => {
            const data = com.data_disponibilizacao?.split('T')[0];
            if (data) {
              porData[data] = (porData[data] || 0) + 1;
            }
          });

          console.log('📅 Comunicações por data:');
          Object.entries(porData)
            .sort(([a], [b]) => b.localeCompare(a))
            .forEach(([data, count]) => {
              const dataFormatada = new Date(data).toLocaleDateString('pt-BR');
              console.log(`  ${dataFormatada} (${data}): ${count} comunicação(ões)`);
            });

          // Debug detalhado das primeiras 3 comunicações
          console.log('🔍 DEBUG: Primeiras 3 comunicações da API:');
          comunicacoes.slice(0, 3).forEach((com, index) => {
            console.log(`📄 Comunicação ${index + 1}:`, {
              id: com.id,
              data_disponibilizacao: com.data_disponibilizacao,
              data_publicacao: com.data_publicacao,
              data_publicacao_dou: com.data_publicacao_dou,
              data_intimacao: com.data_intimacao,
              numeroProcesso: com.numeroProcesso || com.numeroprocessocommascara,
              todosOsCamposData: Object.keys(com).filter(key => key.includes('data'))
            });
          });
        }

        console.log(`✅ ${comunicacoes.length} comunicações encontradas para busca`);
        if (comunicacoes.length > 0) {
          console.log('📄 Primeira comunicação encontrada:', comunicacoes[0]);
        } else {
          console.log('📭 Nenhuma comunicação encontrada para os parâmetros informados');
        }
        return comunicacoes;
      } else {
        console.warn('⚠️ Resposta inesperada da API PJe:', data);
        console.warn('⚠️ data.items não é um array:', typeof data.items);
        return [];
      }
    } catch (error) {
      console.error('Erro ao buscar comunicações PJe:', error);
      throw error;
    }
  }

  /**
   * Configurar sincronização automática para um usuário
   */
  static setupAutomaticSync(userId, syncSchedule) {
    console.log(`⏰ Configurando sincronização automática para usuário ${userId}:`, syncSchedule);
    
    // Limpar timers existentes
    this.clearUserTimers(userId);
    
    // Configurar novos timers
    const timers = [];
    
    for (const time of syncSchedule) {
      const timer = this.scheduleDaily(time, async () => {
        try {
          console.log(`🔄 Executando sincronização automática às ${time} para usuário ${userId}`);
          
          // Buscar OABs do usuário
          const oabs = await FirebaseService.getUserOABs(userId);
          
          if (oabs.length > 0) {
            await this.executeDailySync(userId, oabs);
            console.log(`✅ Sincronização automática concluída às ${time} para usuário ${userId}`);
          } else {
            console.warn(`⚠️ Nenhuma OAB encontrada para usuário ${userId}`);
          }
        } catch (error) {
          console.error(`❌ Erro na sincronização automática às ${time} para usuário ${userId}:`, error);
          
          // Agendar para background sync como fallback
          try {
            await backgroundSyncService.scheduleBackgroundSync(userId, 'daily');
            console.log(`📱 Background sync agendado como fallback para usuário ${userId}`);
          } catch (bgError) {
            console.error(`❌ Erro ao agendar background sync:`, bgError);
          }
        }
      });
      
      timers.push(timer);
    }
    
    // Armazenar timers para poder limpar depois
    if (!this.userTimers) {
      this.userTimers = new Map();
    }
    this.userTimers.set(userId, timers);
    
    return timers;
  }

  /**
   * Agendar execução diária em um horário específico
   */
  static scheduleDaily(time, callback) {
    const [hours, minutes] = time.split(':').map(Number);
    
    const scheduleNext = () => {
      const now = new Date();
      const scheduledTime = new Date();
      scheduledTime.setHours(hours, minutes, 0, 0);
      
      // Se o horário já passou hoje, agendar para amanhã
      if (scheduledTime <= now) {
        scheduledTime.setDate(scheduledTime.getDate() + 1);
      }
      
      const delay = scheduledTime.getTime() - now.getTime();
      
      return setTimeout(() => {
        callback();
        // Reagendar para o próximo dia
        scheduleNext();
      }, delay);
    };
    
    return scheduleNext();
  }

  /**
   * Limpar timers de um usuário
   */
  static clearUserTimers(userId) {
    if (this.userTimers && this.userTimers.has(userId)) {
      const timers = this.userTimers.get(userId);
      timers.forEach(timer => clearTimeout(timer));
      this.userTimers.delete(userId);
      console.log(`🧹 Timers limpos para usuário ${userId}`);
    }
  }

  /**
   * Limpar todos os timers
   */
  static clearAllTimers() {
    if (this.userTimers) {
      this.userTimers.forEach((timers, userId) => {
        timers.forEach(timer => clearTimeout(timer));
        console.log(`🧹 Timers limpos para usuário ${userId}`);
      });
      this.userTimers.clear();
    }
  }

  /**
   * Verificar status da sincronização de um usuário
   */
  static async getSyncStatus(userId) {
    try {
      const settings = await FirebaseService.getMonitoringSettings(userId);
      const oabs = await FirebaseService.getUserOABs(userId);
      const comunicacoes = await FirebaseService.getUserPjeComunicacoes(userId, { limit: 10 });

      // Verificar se há timers ativos
      const hasActiveTimers = this.userTimers && this.userTimers.has(userId);

      // Verificar última sincronização
      let lastSync = null;
      let syncedToday = false;

      if (comunicacoes.length > 0) {
        lastSync = comunicacoes[0].fetchedAt;

        // Verificar se houve sincronização hoje (com validação de data)
        if (lastSync) {
          try {
            const hoje = new Date().toISOString().split('T')[0];
            const lastSyncDate = new Date(lastSync);

            // Verificar se a data é válida
            if (!isNaN(lastSyncDate.getTime())) {
              const lastSyncDateStr = lastSyncDate.toISOString().split('T')[0];
              syncedToday = lastSyncDateStr === hoje;
            }
          } catch (error) {
            console.warn('⚠️ Erro ao processar data de sincronização:', error);
            lastSync = null;
          }
        }
      }

      return {
        isConfigured: !!settings,
        oabsCount: oabs.length,
        lastSync,
        syncedToday,
        totalComunicacoes: comunicacoes.length,
        syncSchedule: settings?.syncSchedule || [],
        autoSyncEnabled: settings?.autoSync || false,
        hasActiveTimers,
        nextSyncTimes: this.getNextSyncTimes(settings?.syncSchedule || [])
      };
    } catch (error) {
      console.error('Erro ao verificar status de sincronização:', error);
      return {
        isConfigured: false,
        oabsCount: 0,
        lastSync: null,
        syncedToday: false,
        totalComunicacoes: 0,
        syncSchedule: [],
        autoSyncEnabled: false,
        hasActiveTimers: false,
        nextSyncTimes: []
      };
    }
  }

  /**
   * Calcular próximos horários de sincronização
   */
  static getNextSyncTimes(syncSchedule) {
    if (!syncSchedule || syncSchedule.length === 0) return [];

    const now = new Date();
    const today = new Date(now);
    const tomorrow = new Date(now);
    tomorrow.setDate(tomorrow.getDate() + 1);

    const nextTimes = [];

    for (const time of syncSchedule) {
      const [hours, minutes] = time.split(':').map(Number);

      // Horário de hoje
      const todayTime = new Date(today);
      todayTime.setHours(hours, minutes, 0, 0);

      // Horário de amanhã
      const tomorrowTime = new Date(tomorrow);
      tomorrowTime.setHours(hours, minutes, 0, 0);

      // Se ainda não passou hoje, usar hoje; senão, usar amanhã
      const nextTime = todayTime > now ? todayTime : tomorrowTime;

      nextTimes.push({
        time,
        nextExecution: nextTime,
        timeUntil: Math.ceil((nextTime - now) / (1000 * 60)), // minutos
        isToday: nextTime.toDateString() === today.toDateString()
      });
    }

    return nextTimes.sort((a, b) => a.nextExecution - b.nextExecution);
  }

  /**
   * Processar comunicações com IA automaticamente
   * SEMPRE HABILITADO - prazos são o objetivo principal do sistema
   */
  static async processWithAIIfEnabled(userId, comunicacoes) {
    try {
      // Sempre processar com IA - é o objetivo principal do sistema
      // O advogado precisa dos prazos detectados automaticamente
      console.log(`🤖 Iniciando processamento automático com IA para ${comunicacoes.length} comunicações`);

      let processedCount = 0;

      // Processar comunicações em lotes pequenos para evitar sobrecarga
      for (let i = 0; i < comunicacoes.length; i++) {
        try {
          await ProcessConversionService.convertComunicacaoToProcesso(userId, comunicacoes[i]);
          processedCount++;

          // Delay entre processamentos para evitar rate limiting
          await this.delay(1500);
        } catch (error) {
          console.error(`❌ Erro ao processar comunicação ${comunicacoes[i].id} automaticamente:`, error);
          // Continuar processando as outras mesmo se uma falhar
        }
      }

      console.log(`✅ Processamento automático concluído: ${processedCount}/${comunicacoes.length} comunicações processadas`);
      return processedCount;

    } catch (error) {
      console.error('❌ Erro no processamento automático com IA:', error);
      return 0;
    }
  }

  /**
   * Delay helper
   */
  static delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Inicializar sincronização para usuário logado
   */
  static async initializeUserSync(userId) {
    try {
      const settings = await FirebaseService.getMonitoringSettings(userId);
      
      if (settings && settings.autoSync && settings.syncSchedule) {
        console.log(`🚀 Inicializando sincronização automática para usuário ${userId}`);
        this.setupAutomaticSync(userId, settings.syncSchedule);
        return true;
      } else {
        console.log(`⚠️ Sincronização automática não configurada para usuário ${userId}`);
        return false;
      }
    } catch (error) {
      console.error(`❌ Erro ao inicializar sincronização para usuário ${userId}:`, error);
      return false;
    }
  }

  /**
   * Parar sincronização para usuário
   */
  static stopUserSync(userId) {
    console.log(`⏹️ Parando sincronização para usuário ${userId}`);
    this.clearUserTimers(userId);
  }
}

// Limpar timers quando a página for fechada
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    SyncService.clearAllTimers();
  });
}
